import {Component, EventEmitter, Inject, Input, Output} from '@angular/core';
import {BaseConfig} from 'src/app/base/models/baseConfig';
import {defaultFileConfig, FileConfig, selfRoutes} from "../../models/config.model";
import {defaultPageV2, IPage, IPageV2, toPage} from "../../../../../../../base/models/pagination.model";
import {EventBusService} from "../../../../../../../base/services/eventbus/event-bus.service";
import {ActivatedRoute, Router} from "@angular/router";
import {SubscriptionContainerService} from "../../../../../../../base/services/subscription-container.service";
import {AuthService} from "../../../../auth/services/auth.service";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {NotifierService} from "../../../../../../../base/services/notifier/notifier.service";
import {
  BasicFilterData,
  defaultFilterData,
  parseFilterData
} from "../../../../utility/components/basic-filter/basic-filter.model";
import {BreadcrumbItem} from "../../../../generic/breadcrumb/breadcrumb.model";
import {IParams} from "../../../../../../../base/endpoints/iparams";
import {EventKey} from "../../../../../../../registry/event.key";
import {MetaData} from "ng-event-bus/lib/meta-data";
import {ComponentType} from "@angular/cdk/overlay";
import {FileUpsertComponent} from "../file-upsert/file-upsert.component";
import {FileRequisitionDefinition, IImage, IImageSlice} from "../../models/file.model";
import {TextUtility} from "../../../../utility/text.utility";
import {BaseDialogComponent} from "../../../../../../../base/components/base-dialog.component";
import {BaseListComponentV2} from "../../../../../../../base/components/base-list-v2.component";
import {DialogService} from "../../../../utility/components/popup/dialog.service";
import {BaseUpsertComponentV2} from "../../../../../../../base/components/base-upsert-v2.component";
import {CmsEndpoint} from "../../../cms.endpoint";
import {FileServiceV2} from "../../services/files-v2.service";

@Component({
  selector: 'app-file-list',
  templateUrl: './file-list.component.html',
  styleUrls: ['./file-list.component.scss']
})
export class FileListComponent extends BaseListComponentV2<IImageSlice[]> implements BaseDialogComponent<IImage[]> {

  config: FileConfig = defaultFileConfig
  // files: IPage<IFile> = defaultPage
  images: IPageV2<IImage> = defaultPageV2
  selectedFiles: Map<IImage, boolean> = new Map<IImage, boolean>()

  @Input() fileRequisitionDef: FileRequisitionDefinition

  @Output() onCanceled: EventEmitter<void> = new EventEmitter<void>()
  @Output() onCompleted: EventEmitter<IImage[] | null> = new EventEmitter<IImage[] | null>();

  legacyPage(): IPage<IImage> {
    return toPage(this.images);
  }

  protected componentName(): string {
    return "FileListComponent";
  }

  constructor(eventBus: EventBusService,
              router: Router,
              subContainer: SubscriptionContainerService,
              private authService: AuthService,
              dialog: DialogService<any>,
              private activatedRoute: ActivatedRoute,
              private fileService: FileServiceV2,
              private notifier: NotifierService,
              @Inject(MAT_DIALOG_DATA) private dialogData: any
  ) {
    super(eventBus, router, subContainer, dialog)
    this.authService.isAdminAsync().subscribe(isAdmin => {
      if (isAdmin) {
        this.config.searchEndpoint = CmsEndpoint.FILES.IMAGE_ENDPOINT_ADMIN
        this.config.imageEndpoint = CmsEndpoint.FILES.IMAGE_ENDPOINT_ADMIN
      }
    })
    this.fileRequisitionDef = dialogData.fileRequisitionDef
  }

  isAdmin() {
    return this.authService.isAdmin()
  }

  isWithinDialog() {
    return this.fileRequisitionDef != null
  }

  filterData: BasicFilterData

  initialize(): void {
    if (this.isWithinDialog()) {
      this.filterData = defaultFilterData
      this.fetchWith(this.filterData)
    } else {
      this.activatedRoute.queryParams.subscribe(params => {
        this.filterData = parseFilterData(params, 12)
        this.fetchWith(this.filterData)
      });
    }
  }

  onFilterClick(filterData: BasicFilterData) {
    // this will be invoked only when useParamNavigation is false
    this.filterData = filterData
    this.fetchWith(this.filterData)
  }

  onNextClick() {
    this.filterData = {
      ...this.filterData,
      page: this.filterData.page + 1
    }
    this.fetchWith(this.filterData)
  }

  onPreviousClick() {
    this.filterData = {
      ...this.filterData,
      page: this.filterData.page - 1
    }
    this.fetchWith(this.filterData)
  }

  protected override getBreadcrumbItems(): BreadcrumbItem[] {
    return [
      {
        text: this.translations["Dashboard"],
        link: null,
        active: false
      },
      {
        text: "Files",
        link: null,
        active: true
      }
    ]
  }

  private fetchWith(filterData: BasicFilterData) {
    this.fetchImages({
      username: filterData.username,
      from_date: filterData.fromDate,
      to_date: filterData.toDate,
      page: filterData.page,
      size: filterData.size
    })
  }

  private fetchImages(params: IParams) {
    this.loading = true
    const key = this.componentKey(EventKey.FILES.SEARCH_IMAGES(params))
    this.fileService.search(this.config.imageEndpoint, params, files => {
      this.loading = false
      this.images = files
    })
    this.subscribe(
      key,
      null,
      (value: Boolean, meta: MetaData) => {
        this.images = meta.data
        this.loading = false
      })
    this.subscribe(this.getErrorKey(key), null, (value, meta) => {
      this.notifier.error('Error', meta.data.error.message)
    })
  }

  onDeleted(deleted: Boolean) {
    if (deleted) {
      this.notifier.success("Success", "File is deleted")
      this.ngOnInit()
    }
  }

  override getConfig(): BaseConfig {
    return this.config
  }

  override getRoutes(): { create: string, update: (id: number) => string, params: IParams } | null {
    return {
      create: selfRoutes.create,
      update: (id: number) => selfRoutes.update(id),
      params: {}
    }
  }

  getUpsertComponent(): ComponentType<BaseUpsertComponentV2<IImageSlice[]>> | null {
    return FileUpsertComponent
  }

  protected readonly TextUtility = TextUtility;

  isSelected(image: IImage): boolean {
    return this.selectedFiles.get(image) == true
  }

  useSelectedFiles() {
    let images = Array.from(this.selectedFiles.entries())
      .filter(([key, value]) => value)
      .map(([key, value]) => key)
    this.clearSelection()
    this.onCompleted.emit(images)
  }

  clearSelection() {
    this.selectedFiles = new Map<IImage, boolean>()
  }

  isAnySelected(): boolean {
    return Array.from(this.selectedFiles.values()).some(v => v)
  }

  toggleFileSelection(image: IImage) {
    if (!this.fileRequisitionDef.multiSelect) {
      this.selectedFiles.clear()
    }
    this.selectedFiles.set(image, !this.isSelected(image))
  }

  onCloseClicked() {
    this.clearSelection()
    this.onCanceled.emit()
  }

}
